'use es6';

import { useCallback, createRef, useEffect, useState, useMemo } from 'react';
import { findDOMNode } from 'react-dom';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { BATTLESHIP, GYPSUM, LORAX } from 'HubStyleTokens/colors';
import { TOOLTIP_SHADOW } from 'HubStyleTokens/misc';
import { GRID_BREAKPOINT_SMALL, GRID_BREAKPOINT_MEDIUM, GRID_BREAKPOINT_LARGE, TAB_INDICATOR_HEIGHT, MARS_LAYER } from 'HubStyleTokens/sizes';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import Big from 'UIComponents/elements/Big';
import Small from 'UIComponents/elements/Small';
import H1 from 'UIComponents/elements/headings/H1';
import UIButton from 'UIComponents/button/UIButton';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIImage from 'UIComponents/image/UIImage';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIMediaObject from 'UIComponents/layout/UIMediaObject';
import UIBreadcrumbs from 'UIComponents/nav/UIBreadcrumbs';
import UITab from 'UIComponents/nav/UITab';
import UITabs from 'UIComponents/nav/UITabs';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import UIRouterLink from 'ui-addon-react-router-dom/UIRouterLink';
import * as CategorySlug from 'ecosystem-marketplace-core/constants/urlSlugs/CategorySlug';
import * as SuperCategorySlug from 'ecosystem-marketplace-core/constants/urlSlugs/SuperCategorySlug';
import MediaQuery from 'marketplace-ui-common/layout/components/MediaQuery';
import * as ScreenSizes from 'ecosystem-marketplace-core/constants/layout/ScreenSizes';
import { getHasAppCertificationCredential } from 'marketplace-ui-apps-core/common/utils/Certification';
import { getSuperCategoryI18nKey } from 'marketplace-ui-apps-core/common/utils/i18n';
import { useResponsiveProps } from 'marketplace-ui-common/hooks/useResponsiveProps';
import { useWindowDimensionsContext } from 'marketplace-ui-common/hooks/useWindowDimensionsContext';
import { getCategoryI18nKey } from 'marketplace-ui-common/utils/i18n';
import { MAIN_SECTION_PADDING_X, MAIN_SECTION_MAX_WIDTH, DEFAULT_ICON_SIZE, SMALL_ICON_SIZE } from '../../constants';
import * as CustomPropTypes from '../../constants/PropTypes';
import { getFormattedUrl } from '../../utils/urls';
import { getEventSelectorId } from '../../utils/analytics';
import ConnectButton from './connect/ConnectButton';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const GRID_BREAKPOINT_MEDIUM_INT = parseInt(GRID_BREAKPOINT_MEDIUM, 10);
const GRID_BREAKPOINT_LARGE_INT = parseInt(GRID_BREAKPOINT_LARGE, 10);
const ICON_GUTTER = 4;
const ICON_SIZE = DEFAULT_ICON_SIZE;
const STICKY_ICON_SIZE = SMALL_ICON_SIZE;
const ICON_SIZE_DIFF = DEFAULT_ICON_SIZE - STICKY_ICON_SIZE;
const STICKY_HEADER_HEIGHT = 60;
const TAB_INDICATOR_HEIGHT_INT = parseInt(TAB_INDICATOR_HEIGHT, 10);
const TABS_PADDING_TOP = 12;
export const TAB_IDS = {
  OVERVIEW: 'overview',
  FEATURES: 'features',
  SHARED_DATA: 'sharedData',
  PRICING: 'pricing',
  REVIEWS: 'reviews'
};
const textPath = 'appMarketplace.detail.header';
const Wrapper = styled.div.withConfig({
  displayName: "Header__Wrapper",
  componentId: "sc-1065q3u-0"
})([".app-detail__header__inner{display:flex;flex-direction:row;align-items:stretch;}.app-detail__header__icon{flex:0 0 auto;order:1;margin-right:", "px;width:", "px;}.app-detail__mobile__header__icon{width:", "px;}.app-detail__header__heading{flex:1 1;order:2;}.app-detail__header__buttons{flex:0 0 auto;order:3;display:flex;flex-wrap:wrap;@media (max-width:1000px){flex:1 1 100%;margin-top:16px;flex-direction:row-reverse;justify-content:flex-end;>:last-child{margin-left:0;}>:not(:last-child){margin-left:12px;}}@media (max-width:", "){flex-direction:column-reverse;}}.app-detail__header__tabs{flex:1 1 100%;order:4;align-self:auto;padding-top:", "px;}&.is--sticky{.app-detail__header__inner{position:fixed;top:0;left:", ";z-index:", ";padding:0 ", "px;width:", ";height:", "px;background-color:", ";border-bottom:1px solid ", ";box-shadow:", ";&__flex{margin:0 auto;width:100%;max-width:", "px;}}.app-detail__header__icon{width:", "px;}.app-detail__header__tabs{flex-basis:auto;order:2;align-self:flex-end;}.app-detail__header__buttons{flex:0 0 auto;align-items:center;margin-top:0;}}"], ICON_GUTTER, ICON_SIZE, STICKY_ICON_SIZE, GRID_BREAKPOINT_SMALL, TABS_PADDING_TOP, props => props.isInApp ? 'var(--global-nav-vertical-nav-width)' : 0, MARS_LAYER, MAIN_SECTION_PADDING_X / 2, props => props.isInApp ? 'calc(100% - var(--global-nav-vertical-nav-width, 236px))' : '100%', STICKY_HEADER_HEIGHT, GYPSUM, BATTLESHIP, TOOLTIP_SHADOW, MAIN_SECTION_MAX_WIDTH, STICKY_ICON_SIZE);
const Header = ({
  activeTab,
  appId,
  appName,
  appUrl,
  breadcrumbs,
  category,
  credentials,
  connectionType,
  headingLevel,
  iconUrl,
  installability,
  isPreview,
  isDataSyncApp,
  listingId,
  onConnectButtonClick,
  onTrackEvent,
  onTrackInteraction,
  onTrackPageview,
  scrollToTabSection,
  showTabs,
  showConnectButton,
  showSetUpUrl,
  setupUrl,
  superCategory,
  tagline,
  isCustomer,
  wrapperStyle
}) => {
  const [sticky, setSticky] = useState(false);
  const [stickyOffsetTop, setStickyOffsetTop] = useState(null);
  const [headerHeight, setHeaderHeight] = useState(null);
  const headerWrapperRef = /*#__PURE__*/createRef();
  const breadcrumbsWrapperRef = /*#__PURE__*/createRef();
  const headingWrapperRef = /*#__PURE__*/createRef();
  const iconWrapperRef = /*#__PURE__*/createRef();
  const tabsWrapperRef = /*#__PURE__*/createRef();
  const categorySlug = CategorySlug[category];
  const superCategorySlug = SuperCategorySlug[superCategory];
  const {
    isMobileView
  } = useResponsiveProps({
    isMobileView: {
      [ScreenSizes.XS]: true,
      [ScreenSizes.MD]: false
    }
  });
  const {
    width: windowWidth
  } = useWindowDimensionsContext();
  const isScreenLargeOrSmaller = windowWidth <= GRID_BREAKPOINT_LARGE_INT;
  const isScreenMediumOrLarger = windowWidth >= GRID_BREAKPOINT_MEDIUM_INT;
  const tabClasses = sticky && isScreenLargeOrSmaller ? 'p-x-3' : "";
  const renderedConnectButton = useMemo(() => {
    return isPreview ? /*#__PURE__*/_jsx(UIButton, {
      use: "primary",
      disabled: true,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `${textPath}.connectAppButton.text`
      })
    }) : /*#__PURE__*/_jsx(ConnectButton, {
      appId: appId,
      appName: appName,
      appUrl: appUrl,
      connectionType: connectionType,
      isCustomer: isCustomer,
      isDataSyncApp: isDataSyncApp,
      listingId: listingId,
      installability: installability,
      onConnectButtonClick: onConnectButtonClick,
      onTrackEvent: onTrackEvent,
      onTrackInteraction: onTrackInteraction,
      onTrackPageview: onTrackPageview,
      sticky: sticky
    });
  }, [appId, appName, appUrl, connectionType, installability, isCustomer, isPreview, isDataSyncApp, listingId, onConnectButtonClick, onTrackEvent, onTrackInteraction, onTrackPageview, sticky]);
  const renderedSetUpUrl = useMemo(() => /*#__PURE__*/_jsx(UIButton, {
    external: true,
    href: getFormattedUrl(setupUrl),
    onClick: () => onTrackInteraction('click view setup guide', 'header'),
    size: sticky ? 'small' : 'default',
    use: "transparent",
    "data-hs-analytics-id": getEventSelectorId('HEADER-SETUP-GUIDE'),
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: `${textPath}.viewSetupGuideLinkText`
    })
  }), [onTrackInteraction, setupUrl, sticky]);
  const renderedButtonRow = useMemo(() => /*#__PURE__*/_jsxs("div", {
    className: "app-detail__header__buttons",
    children: [showSetUpUrl && renderedSetUpUrl, showConnectButton && renderedConnectButton]
  }), [renderedConnectButton, renderedSetUpUrl, showConnectButton, showSetUpUrl]);
  const trackBreadcrumbLinkClick = useCallback(value => {
    onTrackInteraction('click breadcrumb link', 'header', value);
  }, [onTrackInteraction]);
  const trackTabClick = useCallback(tabId => {
    onTrackInteraction('click tab navigation', 'header', tabId);
  }, [onTrackInteraction]);
  const setBreadcrumbsScrollState = useCallback(scrollPercent => {
    const {
      current: breadcrumbsWrapper
    } = breadcrumbsWrapperRef;
    if (breadcrumbsWrapper) {
      breadcrumbsWrapper.style.opacity = 1 - scrollPercent;
    }
  }, [breadcrumbsWrapperRef]);
  const setHeadingScrollState = useCallback(scrollPercent => {
    const {
      current: headingWrapper
    } = headingWrapperRef;
    if (headingWrapper) {
      headingWrapper.style.opacity = 1 - scrollPercent;
    }
  }, [headingWrapperRef]);
  const setIconWrapperScrollState = useCallback(scrollPercent => {
    const {
      current: iconWrapper
    } = iconWrapperRef;
    if (iconWrapper) {
      const isAnimating = scrollPercent > 0 && scrollPercent < 1;
      const iconScrollSize = DEFAULT_ICON_SIZE - ICON_SIZE_DIFF * scrollPercent;
      iconWrapper.style.position = isAnimating ? 'relative' : '';
      iconWrapper.style.top = isAnimating ? `${DEFAULT_ICON_SIZE - iconScrollSize + scrollPercent * TABS_PADDING_TOP}px` : '';
      iconWrapper.style.marginRight = isAnimating ? `${DEFAULT_ICON_SIZE - iconScrollSize + ICON_GUTTER}px` : '';
      iconWrapper.style.width = isAnimating ? `${iconScrollSize}px` : '';
    }
  }, [iconWrapperRef]);
  const setTabsWrapperScrollState = useCallback(scrollPercent => {
    const {
      current: tabsWrapper
    } = tabsWrapperRef;
    if (tabsWrapper) {
      tabsWrapper.style.transform = scrollPercent < 1 ? `translateX(${(STICKY_ICON_SIZE + ICON_GUTTER) * scrollPercent}px)` : '';
    }
  }, [tabsWrapperRef]);
  const defaultBreadcrumbs = useMemo(() => !isPreview ? /*#__PURE__*/_jsxs(UIBreadcrumbs, {
    className: "m-top-0",
    flush: true,
    children: [/*#__PURE__*/_jsx(UIRouterLink, {
      onClick: () => trackBreadcrumbLinkClick('all apps'),
      to: "/",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `${textPath}.rootBreadcrumbLinkText`
      })
    }), superCategory && /*#__PURE__*/_jsx(UIRouterLink, {
      onClick: () => trackBreadcrumbLinkClick(superCategory),
      to: `/${superCategorySlug}`,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: getSuperCategoryI18nKey(superCategory)
      })
    }), category && /*#__PURE__*/_jsx(UIRouterLink, {
      onClick: () => trackBreadcrumbLinkClick(category),
      to: `/${superCategorySlug}/${categorySlug}`,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: getCategoryI18nKey(category)
      })
    }), /*#__PURE__*/_jsx("span", {
      children: appName
    })]
  }) : null, [appName, category, categorySlug, isPreview, superCategory, superCategorySlug, trackBreadcrumbLinkClick]);
  const renderedCertifiedLabel = useMemo(() => {
    if (getHasAppCertificationCredential(credentials)) {
      return /*#__PURE__*/_jsx(UITooltip, {
        placement: "bottom",
        title: unescapedText(`${textPath}.certifiedLabel.tooltip`),
        children: /*#__PURE__*/_jsxs("div", {
          style: {
            display: 'inline-block',
            lineHeight: 1
          },
          children: [/*#__PURE__*/_jsx(UIIcon, {
            className: "m-right-1",
            color: LORAX,
            name: "hubspotCertification",
            size: 24,
            style: {
              position: 'relative',
              top: 2
            }
          }), /*#__PURE__*/_jsx(Small, {
            style: {
              fontWeight: 600,
              textTransform: 'uppercase'
            },
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: `${textPath}.certifiedLabel.text`
            })
          })]
        })
      });
    } else {
      return null;
    }
  }, [credentials]);
  const renderedHeading = useMemo(() => {
    const Heading = headingLevel === 'h2' ? 'span' : H1;
    const headingClasses = 'm-bottom-0' + (headingLevel === 'h2' ? " is--heading-1" : "");
    const ariaLevel = headingLevel === 'h1' ? 1 : null;
    return /*#__PURE__*/_jsx(Heading, {
      "data-test-id": "details-heading",
      "aria-level": ariaLevel,
      className: headingClasses,
      children: appName
    });
  }, [appName, headingLevel]);
  const scrollAnimationFrame = useCallback(() => {
    if (!isScreenMediumOrLarger) {
      setSticky(false);
      return;
    }
    const pageYOffset = document.body.classList.contains('sticky-global-toolbar') ? document.getElementsByClassName('page')[0].scrollTop : window.pageYOffset;
    const {
      offsetTop: headerOffsetTop
    } = findDOMNode(headerWrapperRef.current) || {};
    if (stickyOffsetTop !== null) {
      let scrollPercent;
      if (pageYOffset >= headerOffsetTop && pageYOffset <= stickyOffsetTop) {
        scrollPercent = (pageYOffset - headerOffsetTop) / (stickyOffsetTop - headerOffsetTop);
      } else if (pageYOffset > stickyOffsetTop) {
        scrollPercent = 1;
      } else if (pageYOffset < headerOffsetTop) {
        scrollPercent = 0;
      }
      if (scrollPercent >= 0 && scrollPercent <= 1) {
        setBreadcrumbsScrollState(scrollPercent);
        setHeadingScrollState(scrollPercent);
        setIconWrapperScrollState(scrollPercent);
        setTabsWrapperScrollState(scrollPercent);
      }
      setSticky(scrollPercent === 1);
    }
  }, [isScreenMediumOrLarger, headerWrapperRef, stickyOffsetTop, setBreadcrumbsScrollState, setHeadingScrollState, setIconWrapperScrollState, setTabsWrapperScrollState]);
  useEffect(() => {
    if (!sticky) {
      const {
        offsetTop: tabsOffsetTop
      } = tabsWrapperRef.current || {};
      const {
        offsetHeight: headerRefHeight
      } = findDOMNode(headerWrapperRef.current) || {};
      setStickyOffsetTop(tabsOffsetTop || null);
      setHeaderHeight(headerRefHeight || null);
      setBreadcrumbsScrollState(0);
      setHeadingScrollState(0);
      setIconWrapperScrollState(0);
      setTabsWrapperScrollState(0);
    }
  }, [setBreadcrumbsScrollState, setHeadingScrollState, sticky, tabsWrapperRef, headerWrapperRef, setHeaderHeight, setIconWrapperScrollState, setTabsWrapperScrollState]);
  useEffect(() => {
    const scrollHandler = () => {
      window.requestAnimationFrame(scrollAnimationFrame);
    };
    window.addEventListener('scroll', scrollHandler, true);
    return () => {
      window.removeEventListener('scroll', scrollHandler, true);
    };
  }, [scrollAnimationFrame]);
  useEffect(() => {
    scrollAnimationFrame();
  }, [scrollAnimationFrame]);
  const onTabClick = useCallback((tabName, evt) => {
    evt.preventDefault();
    trackTabClick(tabName);
    return scrollToTabSection(tabName);
  }, [scrollToTabSection, trackTabClick]);
  useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (hash && Object.values(TAB_IDS).includes(hash)) {
      scrollToTabSection(hash);
    }
  }, [scrollToTabSection]);
  return /*#__PURE__*/_jsxs(Wrapper, {
    isInApp: document.body.classList.contains('sticky-global-toolbar'),
    className: "app-detail__header p-top-4" + (sticky ? " is--sticky" : ""),
    style: Object.assign({
      // Adjust based on the non-sticky header height, including nav borders
      paddingBottom: sticky ? headerHeight - STICKY_HEADER_HEIGHT - TAB_INDICATOR_HEIGHT_INT : 0
    }, wrapperStyle),
    ref: headerWrapperRef,
    children: [/*#__PURE__*/_jsx("div", {
      className: "m-bottom-6",
      ref: breadcrumbsWrapperRef,
      children: breadcrumbs || defaultBreadcrumbs
    }), /*#__PURE__*/_jsx("div", {
      className: "app-detail__header__inner",
      children: /*#__PURE__*/_jsxs(UIFlex, {
        align: "center",
        justify: "between",
        className: "app-detail__header__inner__flex",
        wrap: "wrap",
        children: [/*#__PURE__*/_jsx(UIMediaObject, {
          className: "app-detail__header__heading-wrapper",
          itemLeft: !isMobileView && /*#__PURE__*/_jsx("div", {
            className: "app-detail__header__icon",
            ref: iconWrapperRef,
            children: /*#__PURE__*/_jsx(UIImage, {
              src: iconUrl
            })
          }),
          children: !sticky && /*#__PURE__*/_jsxs("div", {
            className: "app-detail__header__heading",
            ref: headingWrapperRef,
            children: [/*#__PURE__*/_jsx(UIMediaObject, {
              align: "center",
              itemLeft: isMobileView && /*#__PURE__*/_jsx("div", {
                className: "app-detail__mobile__header__icon",
                children: /*#__PURE__*/_jsx(UIImage, {
                  src: iconUrl
                })
              }),
              children: renderedHeading
            }), /*#__PURE__*/_jsx("div", {
              className: "m-bottom-1",
              children: /*#__PURE__*/_jsx(Big, {
                children: tagline
              })
            }), renderedCertifiedLabel]
          })
        }), renderedButtonRow, showTabs && /*#__PURE__*/_jsx("div", {
          className: "app-detail__header__tabs",
          ref: tabsWrapperRef,
          children: /*#__PURE__*/_jsx(MediaQuery, {
            minScreenSize: "md",
            children: /*#__PURE__*/_jsx(UITabs, {
              bordered: false,
              defaultSelected: TAB_IDS.OVERVIEW,
              selected: activeTab,
              children: Object.values(TAB_IDS).map(tab => /*#__PURE__*/_jsx(UITab, {
                "data-test-id": `details-tab-${tab}`,
                className: tabClasses,
                onClick: evt => onTabClick(tab, evt),
                tabId: tab,
                title: unescapedText(`${textPath}.tabTitles.${tab}`)
              }, tab))
            })
          })
        })]
      })
    })]
  });
};
Header.propTypes = {
  activeTab: PropTypes.string,
  appId: PropTypes.number,
  appName: PropTypes.string,
  appUrl: PropTypes.string,
  breadcrumbs: PropTypes.node,
  category: CustomPropTypes.category,
  credentials: PropTypes.arrayOf(PropTypes.number).isRequired,
  connectionType: CustomPropTypes.connectionType,
  headingLevel: PropTypes.oneOf(['h1', 'h2']),
  installability: CustomPropTypes.installability,
  iconUrl: PropTypes.string.isRequired,
  isCustomer: PropTypes.bool.isRequired,
  isPreview: PropTypes.bool,
  isDataSyncApp: PropTypes.bool,
  listingId: PropTypes.number,
  onConnectButtonClick: PropTypes.func,
  onTrackEvent: PropTypes.func,
  onTrackInteraction: PropTypes.func,
  onTrackPageview: PropTypes.func,
  tagline: PropTypes.string.isRequired,
  scrollToTabSection: PropTypes.func,
  showTabs: PropTypes.bool.isRequired,
  showConnectButton: PropTypes.bool.isRequired,
  showSetUpUrl: PropTypes.bool.isRequired,
  setupUrl: PropTypes.string,
  superCategory: CustomPropTypes.superCategory,
  wrapperStyle: PropTypes.object
};
Header.defaultProps = {
  headingLevel: 'h1',
  showTabs: true,
  showConnectButton: true,
  showSetUpUrl: true
};
export default Header;